<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

const { t } = useI18n()

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger, SplitText)
  media = gsap.matchMedia()

  media.add('(prefers-reduced-motion: no-preference)', () => {
    const q = gsap.utils.selector(root.value)
    root.value!.classList.add('about-ready')

    // Header: the title rises letter by letter, the photo comes up out of its disc.
    const title = SplitText.create(q('.about-title')[0], { type: 'words,chars', mask: 'chars' })
    gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
      .from(title.chars, { yPercent: 110, duration: 1.1, stagger: 0.04 }, 0)
      .from(q('.about-rule'), { scaleX: 0, duration: 1.2, ease: 'power3.inOut' }, 0.3)
      .from(q('.about-subtitle'), { y: 16, opacity: 0, duration: 0.7, ease: 'power3.out' }, 0.5)
      .from(q('.hero-disc'), { scale: 0.6, opacity: 0, duration: 0.9, ease: 'back.out(1.6)' }, 0.5)
      .from(q('.hero-portrait'), { yPercent: 45, opacity: 0, duration: 1.1, ease: 'power3.out' }, 0.6)

    // The first paragraph lights up word by word while it is read.
    const lead = q('.about-intro p')[0]
    const words = lead ? SplitText.create(lead, { type: 'words' }) : undefined
    if (words) {
      gsap.fromTo(words.words, { opacity: 0.18 }, {
        opacity: 1,
        stagger: 0.1,
        ease: 'none',
        scrollTrigger: { trigger: lead, start: 'top 80%', end: 'bottom 55%', scrub: 0.4 },
      })
    }

    q('.about-intro p:not(:first-child), .about-reveal').forEach((element) => {
      gsap.from(element, { y: 24, opacity: 0, duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 88%', once: true } })
    })

    // The career line draws itself as the page scrolls.
    const line = q('.about-line-fill')[0]
    if (line) {
      gsap.from(line, { scaleY: 0, ease: 'none', scrollTrigger: { trigger: line.parentElement, start: 'top 70%', end: 'bottom 60%', scrub: 0.4 } })
    }

    gsap.from(q('.about-tool'), {
      y: 16,
      opacity: 0,
      duration: 0.5,
      stagger: 0.05,
      ease: 'power3.out',
      scrollTrigger: { trigger: q('.about-tools')[0], start: 'top 85%', once: true },
    })

    return () => {
      title.revert()
      words?.revert()
      root.value?.classList.remove('about-ready')
    }
  }, root.value)
})

onBeforeUnmount(() => media?.revert())

const stack = await queryCollection('stack').first()
</script>

<template>
  <section
    ref="root"
    class="about-root mx-auto max-w-7xl overflow-x-clip px-4 pb-24 sm:px-6 lg:px-8"
  >
    <header class="pt-10 sm:pt-24">
      <h1 class="about-in about-title text-[clamp(4rem,17vw,14rem)] font-semibold leading-[0.95] tracking-tighter text-white">
        <slot
          name="title"
          mdc-unwrap="p"
        />
      </h1>
      <div class="about-in about-rule mt-6 h-px origin-left bg-white/20" />
      <p class="about-in about-subtitle mt-5 text-white/60 sm:text-lg">
        <slot
          name="subtitle"
          mdc-unwrap="p"
        />
      </p>
    </header>

    <!-- intro -->
    <div class="mt-16 grid items-start gap-12 lg:mt-24 lg:grid-cols-12 lg:gap-16">
      <div class="lg:sticky lg:top-24 lg:col-span-4">
        <HomeProfilePicture class="about-in mx-auto !w-full max-w-xs lg:max-w-none" />
      </div>
      <div class="about-intro lg:col-span-8">
        <slot name="intro" />
      </div>
    </div>

    <!-- career -->
    <div class="mt-24 lg:mt-40">
      <slot
        name="experiences"
        mdc-unwrap="p"
      />
    </div>

    <!-- stack -->
    <div class="mt-24 grid gap-10 lg:mt-40 lg:grid-cols-12 lg:gap-16">
      <div class="about-reveal lg:col-span-4">
        <h2 class="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
          <slot
            name="stack_title"
            mdc-unwrap="p"
          />
        </h2>
        <p class="mt-4 text-pretty text-white/60">
          <slot
            name="stack_description"
            mdc-unwrap="p"
          />
        </p>
      </div>
      <ul class="about-tools grid grid-cols-2 gap-x-8 border-t border-white/10 sm:grid-cols-3 lg:col-span-8">
        <li
          v-for="item in stack!.items"
          :key="item.name"
          class="about-tool border-b border-white/10"
        >
          <NuxtLink
            :to="item.link"
            target="_blank"
            rel="noopener"
            class="group flex items-center gap-3 py-4 text-white/70 transition-colors hover:text-white"
          >
            <UIcon
              :name="item.icon"
              class="size-5 shrink-0"
              :font-controlled="false"
            />
            <span class="truncate text-sm font-medium sm:text-base">{{ item.name }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>

    <!-- closing -->
    <div class="about-reveal mt-24 flex flex-col items-start gap-8 border-t border-white/15 pt-16 lg:mt-40 lg:flex-row lg:items-end lg:justify-between">
      <p class="max-w-3xl text-balance text-5xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl xl:text-7xl">
        {{ t("global.project_next") }}
      </p>
      <NuxtLinkLocale
        to="/contact"
        class="group flex h-14 shrink-0 items-center gap-4 rounded-full bg-white pl-6 pr-2 font-medium text-neutral-950"
      >
        {{ t("agency.cta") }}
        <span class="flex size-10 items-center justify-center rounded-full bg-neutral-950 text-white">
          <UIcon
            name="heroicons:arrow-right"
            class="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </span>
      </NuxtLinkLocale>
    </div>
  </section>
</template>

<style>
  /* The intro comes from the Markdown content: the first paragraph is the big statement. */
  .about-intro p {
    margin: 0 0 1.25rem;
    font-size: 1.125rem;
    line-height: 1.7;
    color: rgb(255 255 255 / 0.6);
  }

  .about-intro p:first-child {
    margin-bottom: 3rem;
    font-size: clamp(1.875rem, 4vw, 3rem);
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: white;
  }

  .about-intro strong {
    font-weight: 500;
    color: white;
  }

  .about-intro p:first-child strong {
    font-weight: 600;
  }

  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .about-root:not(.about-ready) .about-in {
      opacity: 0;
      animation: about-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes about-fallback {
    to {
      opacity: 1;
    }
  }
</style>
